import { useQueryClient } from '@tanstack/react-query';
import {
  useTrackedMutation as useMutation,
  useTrackedQuery as useQuery,
} from '@ais-ui/performance-tracking';
import { useCallback, useMemo } from 'react';
import { useSession } from '../SessionProvider';
import {
  getReportQueueStatus,
  getReportResult,
  ReportResult,
  startQueryingReportResult,
} from './getReportResult';
import * as Sentry from '@sentry/react';

interface UseCompleteReportFetchingProcessResult {
  isLoading: boolean;
  isError: boolean;
  data: ReportResult | undefined;
  status: string | undefined;
}

// Maximum time to wait for queue to complete (in ms)
const POLL_TIMEOUT_MS = 30000; // 30 seconds

/**
 * Hook to fetch report data using a queue-based approach.
 * 
 * Flow:
 * 1. Start a queue job for the given jobId
 * 2. Poll the queue status until complete
 * 3. Fetch the results once queue is complete
 * 
 * All state is managed by React Query - no local state needed.
 * When jobId changes, React Query automatically handles cache invalidation.
 */
export const useCompleteReportFetchingProcess = (
  jobId: string,
  timeZone: string
): UseCompleteReportFetchingProcessResult => {
  const { dbSession } = useSession();
  const queryClient = useQueryClient();

  // Check if we already have cached results for this jobId
  const existingResults = queryClient.getQueryData<ReportResult>([
    'report',
    'results',
    jobId,
  ]);

  // Step 1: Start the queue job and get a queueId
  // This query runs once per jobId and caches the queueId
  const startQueue = useQuery({
    queryKey: ['report', 'queue', 'start', jobId],
    queryFn: async () => {
      if (!dbSession) {
        throw new Error('dbSession is not defined.');
      }
      console.log('[useFetchReport] Starting queue for jobId:', jobId);
      const queueId = await startQueryingReportResult(dbSession, jobId);
      return { queueId, startTime: Date.now() };
    },
    enabled: !!jobId && !!dbSession && !existingResults,
    staleTime: Infinity, // Queue ID doesn't go stale
    gcTime: 1000 * 60 * 5, // Keep for 5 minutes
    retry: 1,
  }, 'report:startQueue');

  const queueId = startQueue.data?.queueId;
  const startTime = startQueue.data?.startTime;

  // Step 2: Poll the queue status
  const pollStatus = useQuery({
    queryKey: ['report', 'queue', 'status', jobId, queueId],
    queryFn: async () => {
      if (!dbSession || !queueId) {
        throw new Error('Missing dbSession or queueId');
      }

      // Check for timeout
      if (startTime && Date.now() - startTime > POLL_TIMEOUT_MS) {
        const elapsedMs = Date.now() - startTime;
        console.warn('[useFetchReport] Poll timeout for jobId:', jobId);
        
        Sentry.captureException(
          new Error('Report loading timed out'),
          {
            tags: {
              'report.queueId': queueId,
              'report.jobId': jobId,
            },
            extra: { queueId, jobId, elapsedMs, timeoutMs: POLL_TIMEOUT_MS },
          }
        );
        
        return { status: 'TimedOut', percentComplete: 0, errorMessage: 'Timeout' };
      }

      try {
        const status = await getReportQueueStatus(dbSession, queueId);
        console.log('[useFetchReport] Poll status:', { jobId, queueId, status: status.status });
        return status;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        console.warn('[useFetchReport] Poll error:', { jobId, queueId, error: errorMessage });
        
        // Treat transient errors as "still running" to continue polling
        if (errorMessage.includes('DataNotLoaded') || errorMessage.includes('InternalError')) {
          return { status: 'QueueRunning', percentComplete: 0, errorMessage: '' };
        }
        throw error;
      }
    },
    enabled: !!queueId && !!dbSession && !existingResults,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      // Stop polling when complete, timed out, or errored
      if (status === 'QueueComplete' || status === 'TimedOut' || query.state.error) {
        return false;
      }
      return 1000;
    },
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false,
    retry: false,
  }, 'report:pollStatus');

  const isQueueComplete = pollStatus.data?.status === 'QueueComplete';
  const isTimedOut = pollStatus.data?.status === 'TimedOut';

  // Step 3: Fetch results once queue is complete
  // NOTE: The results cache key ['report', 'results', jobId] is used by useGetExtraJobData
  // to determine when it's safe to fetch extra job data. If you change this key structure,
  // update useGetExtraJobData.ts accordingly.
  const fetchResults = useQuery({
    queryKey: ['report', 'results', jobId],
    queryFn: async () => {
      if (!dbSession || !queueId) {
        throw new Error('Missing dbSession or queueId');
      }
      console.log('[useFetchReport] Fetching results for jobId:', jobId);
      return await getReportResult(dbSession, queueId, timeZone);
    },
    enabled: isQueueComplete && !!queueId && !!dbSession && !existingResults,
    staleTime: Infinity,
    gcTime: 1000 * 60 * 30, // Keep results cached for 30 minutes
  }, 'report:fetchResults');

  // Derive the final result from query states
  const result = useMemo((): UseCompleteReportFetchingProcessResult => {
    // Return cached results immediately
    if (existingResults) {
      return {
        isLoading: false,
        isError: false,
        data: existingResults,
        status: 'QueueComplete',
      };
    }

    // Handle timeout
    if (isTimedOut) {
      return {
        isLoading: false,
        isError: true,
        data: undefined,
        status: 'TimedOut',
      };
    }

    // Aggregate loading states
    const isLoading = startQueue.isLoading || pollStatus.isLoading || fetchResults.isLoading ||
      (pollStatus.data?.status === 'QueueRunning') ||
      (isQueueComplete && !fetchResults.data);

    // Aggregate error states
    const isError = startQueue.isError || pollStatus.isError || fetchResults.isError;

    return {
      isLoading: isLoading && !isError,
      isError,
      data: fetchResults.data,
      status: pollStatus.data?.status,
    };
  }, [
    existingResults,
    isTimedOut,
    isQueueComplete,
    startQueue.isLoading,
    startQueue.isError,
    pollStatus.isLoading,
    pollStatus.isError,
    pollStatus.data?.status,
    fetchResults.isLoading,
    fetchResults.isError,
    fetchResults.data,
  ]);

  return result;
};

/**
 * Invalidate all report-related queries for a specific jobId.
 * Useful when you need to force a refetch.
 */
export const useInvalidateReportQueries = () => {
  const queryClient = useQueryClient();
  
  return useCallback((jobId: string) => {
    queryClient.invalidateQueries({ queryKey: ['report', 'queue', 'start', jobId] });
    queryClient.invalidateQueries({ queryKey: ['report', 'queue', 'status', jobId] });
    queryClient.invalidateQueries({ queryKey: ['report', 'results', jobId] });
  }, [queryClient]);
};
